import { useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { DiscountType } from '../../shared/types/discount.type';
import { UserDiscount } from '../types/user-discount.type';
import { USER_DISCOUNTS_API_PATHS } from '../constants/users-paths.constants';

interface NextDiscountCodeResponse {
  data: string;
}

interface CreateDiscountResponse {
  data: {
    discount: UserDiscount;
  };
}

export type CreateUserDiscountPayload = {
  name: string;
  value: number;
  reduction: DiscountType;
  owner: string;
};

type UseCreateUserDiscountResult = {
  isCreatingDiscount: boolean;
  getNextDiscountCode: () => Promise<string | null>;
  createDiscount: (payload: CreateUserDiscountPayload) => Promise<UserDiscount | null>;
};

export function useCreateUserDiscount(): UseCreateUserDiscountResult {
  const { enqueueSnackbar } = useSnackbar();
  const [isCreatingDiscount, setIsCreatingDiscount] = useState(false);

  const getNextDiscountCode = async (): Promise<string | null> => {
    try {
      const response = await HttpClient.get<NextDiscountCodeResponse>(USER_DISCOUNTS_API_PATHS.GET_NEXT_DISCOUNT_CODE);
      return response.data;
    } catch (error) {
      enqueueSnackbar(extractTranslatedApiErrorMessage(error, 'Erreur lors de la génération du code'), {
        variant: 'error',
      });
      return null;
    }
  };

  const createDiscount = async (payload: CreateUserDiscountPayload): Promise<UserDiscount | null> => {
    setIsCreatingDiscount(true);
    try {
      const response = await HttpClient.post<CreateDiscountResponse>(USER_DISCOUNTS_API_PATHS.CREATE_DISCOUNT, payload);
      enqueueSnackbar("Le bon d'achat a bien été créé", { variant: 'success' });
      return response.data.discount;
    } catch (error) {
      enqueueSnackbar(extractTranslatedApiErrorMessage(error, "Erreur lors de la création du bon d'achat"), {
        variant: 'error',
      });
      return null;
    } finally {
      setIsCreatingDiscount(false);
    }
  };

  return { isCreatingDiscount, getNextDiscountCode, createDiscount };
}
